(ns frontend.view.modals.entity.events
  (:require
    [clojure.string :as string]
    [common.email :as email]
    [common.localization :as loc]
    [common.schema.entities.pipeline :as pipeline]
    [common.schema.entities.survey.survey-template :as survey-template-schema]
    [common.schema.registry :as registry]
    [common.util :as cu :refer [remove-empty-vals]]
    [frontend.actions.entity :as entity-actions]
    [frontend.db :as app-db]
    [frontend.logging :as logging]
    [frontend.state.current-session :as session]
    [ui.state.named-values :as named-values]
    [frontend.state.current-settings :as current-settings]
    [frontend.state.permissions :as f-permissions]
    [frontend.tenant-customizations :as tenant-customizations]
    [frontend.utils :as fu]))

(defn multiple-pipelines?
  [entity-type]
  (cond (= entity-type :entity.type/oppo)
        (cu/multiple? @session/all-oppo-pipelines)
        (= entity-type :entity.type/fundraising-opportunity)
        (cu/multiple? @session/all-froppo-pipelines)
        (= entity-type :entity.type/sales-opportunity)
        (cu/multiple? @session/all-sales-oppo-pipelines)
        (= entity-type :entity.type/exit-opportunity)
        (cu/multiple? @session/all-exit-oppo-pipelines)))

(defn pipeline-view?
  [entity-type creation-pipeline-id]
  (and (multiple-pipelines? entity-type)
       (pipeline/pipeline-entity? entity-type)
       (not creation-pipeline-id)))

(defn pipeline-view-selected-pipeline?
  [draft-state]
  (fu/pipeline-id @draft-state))

(defn sort-by-mandatory-field-first
  [mandatory-fields k]
  (let [tenant-schema @session/tenant-schema]
    (or (->> mandatory-fields
             (map-indexed (fn [idx mk]
                            (when
                              (= mk k)
                              idx)))
             (remove nil?)
             (first))
        (->> k
             (registry/get-field-schema tenant-schema)
             :field/default-ord))))

(defn default-pipeline-id
  [entity-type creation-pipeline-id]
  (case entity-type
    :entity.type/oppo (or creation-pipeline-id
                          @session/selected-oppo-pipeline
                          (cu/first-active-entity-id @session/all-oppo-pipelines))
    :entity.type/fundraising-opportunity (or creation-pipeline-id
                                             @session/selected-froppo-pipeline
                                             (cu/first-active-entity-id
                                               @session/all-froppo-pipelines))
    :entity.type/sales-opportunity (or creation-pipeline-id
                                       @session/selected-sales-oppo-pipeline
                                       (cu/first-active-entity-id
                                         @session/all-sales-oppo-pipelines))
    :entity.type/exit-opportunity (or creation-pipeline-id
                                      @session/selected-exit-oppo-pipeline
                                      (cu/first-active-entity-id
                                        @session/all-exit-oppo-pipelines))))

(defn reset-pipeline-state!
  [{:keys [state draft-state live-updates-state entity-type creation-pipeline-id]}]
  (let [pipeline-key (pipeline/pipeline-key entity-type)
        pipeline-id (default-pipeline-id entity-type creation-pipeline-id)
        initial-value {pipeline-key pipeline-id}]
    (swap! draft-state merge initial-value)
    (swap! live-updates-state merge initial-value)
    (fu/timeout 10
                #(swap! state dissoc :processing))))

(defn reset-state!
  [{:keys [state error-state live-updates-state draft-state alert-state
           entity-type mandatory-fields attribute initial-values start-value
           creation-pipeline-id]}]
  (let [tenant-schema @session/tenant-schema
        current-oppo-pipeline (or creation-pipeline-id
                                  (:oppo/pipeline @draft-state)
                                  @session/selected-oppo-pipeline
                                  (cu/first-active-entity-id @session/all-oppo-pipelines))
        current-fundraising-opportunity-pipeline (or creation-pipeline-id
                                                     (:fundraising-opportunity/pipeline @draft-state)
                                                     @session/selected-froppo-pipeline
                                                     (cu/first-active-entity-id @session/all-froppo-pipelines))
        current-sales-opportunity-pipeline (or creation-pipeline-id
                                               (:sales-opportunity/pipeline @draft-state)
                                               @session/selected-sales-oppo-pipeline
                                               (cu/first-active-entity-id
                                                 @session/all-sales-oppo-pipelines))
        current-exit-opportunity-pipeline (or creation-pipeline-id
                                              (:exit-opportunity/pipeline @draft-state)
                                              @session/selected-exit-oppo-pipeline
                                              (cu/first-active-entity-id
                                                @session/all-exit-oppo-pipelines))
        entity-pipeline-id (or creation-pipeline-id
                               (case entity-type
                                 :entity.type/oppo
                                 current-oppo-pipeline

                                 :entity.type/fundraising-opportunity
                                 current-fundraising-opportunity-pipeline

                                 :entity.type/sales-opportunity
                                 current-sales-opportunity-pipeline

                                 :entity.type/exit-opportunity
                                 current-exit-opportunity-pipeline

                                 nil))
        start-value
        (if-not (map? start-value)
          start-value
          (->> start-value
               (filter
                 (fn [[k _v]]
                   (let [field-schema (registry/get-field-schema tenant-schema k)
                         access? (and (f-permissions/field-write-access? k entity-type entity-pipeline-id)
                                      (or (not (:field/secondary-field? field-schema))
                                          (f-permissions/field-write-access? (:field/related-fields field-schema)
                                                                             entity-type
                                                                             entity-pipeline-id)))]
                     access?)))
               (into {})))]
    (reset! state {:processing true})
    (reset! error-state nil)
    (reset! live-updates-state nil)
    (reset! alert-state nil)
    (reset! draft-state nil)

    (swap! state assoc
           :start-time 0
           :show-all? (or (and (tenant-customizations/capman?)
                               (tenant-customizations/capman-is-real-estate-pipeline?
                                 current-oppo-pipeline))
                          (and (tenant-customizations/voima-ventures?)
                               (some #{entity-type} [:entity.type/oppo])))
           :entity-type entity-type
           :attribute attribute
           :mandatory-fields mandatory-fields)

    (case entity-type
      :entity.type/risk-holding (let [initial-value start-value]
                                  (swap! draft-state merge initial-value)
                                  (swap! live-updates-state merge initial-value))
      :entity.type/kyc-risk-category (let [initial-value (merge {}
                                                                (when (string? start-value)
                                                                  {:kyc-risk-category/name
                                                                   start-value})
                                                                (when (map? start-value)
                                                                  start-value))]
                                       (swap! draft-state merge initial-value)
                                       (swap! live-updates-state merge initial-value))
      :entity.type/oppo
      (let [initial-value
            (merge
              {}
              initial-values
              {:oppo/pipeline current-oppo-pipeline
               :oppo/owner    (let [account-id (:db/id @session/account)
                                    default-id (or
                                                 (when (get @app-db/active-user-account-roles account-id) ;; this should filter out zadmin / sys-admin user
                                                   account-id) ;;TODO : filter out admins that are not pipeline teams' member
                                                 (->> @session/all-accounts
                                                      (filter
                                                        (fn [a]
                                                          (and
                                                            (get @app-db/active-user-account-roles (:db/id a))
                                                            (session/access-to-pipeline a current-oppo-pipeline)))) ;; Verify with @Mikko P - admin cannot be owner unless team member to pipeline teams
                                                      (first) ;; TODO: something more sensible here ? only when zadmin does something
                                                      :db/id))]
                                default-id)
               :oppo/status   :oppo.status/ok
               :oppo/stage    (->> @session/all-stages
                                   (cu/filter-by :stage/pipeline current-oppo-pipeline)
                                   (sort-by :common/ord)
                                   (first)
                                   :db/id)}
              (when-let [fund (some->> current-oppo-pipeline
                                       (get @session/entities)
                                       :pipeline/fund)]
                (when (and (session/has-fund-access? fund)
                           (f-permissions/field-write-access? :oppo/fund :entity.type/oppo current-oppo-pipeline))
                  {:oppo/fund fund}))
              (when (string? start-value)
                {:oppo/name start-value})
              (when (map? start-value)
                start-value)
              ;; Both defaults go through the one shared rule, which also
              ;; resolves the options for an account that is not bulk-synced
              ;; them. NOTE: the lead-source branch used to validate the
              ;; preferred SOURCE against the lead-TYPE options - a copy-paste
              ;; that quietly discarded the preference and fell back to the
              ;; first option by ord. Using the right attribute here changes
              ;; that: a user with a preferred source category now gets it.
              (when (f-permissions/field-write-access? :oppo/lead-type-v2
                                                       :entity.type/oppo
                                                       current-oppo-pipeline)
                {:oppo/lead-type-v2
                 (named-values/preferred-id :oppo/lead-type-v2
                                            current-oppo-pipeline
                                            (->> @session/account
                                                 :account/preferences
                                                 :preferred-lead-type))})
              (when (f-permissions/field-write-access? :oppo/lead-source-v2
                                                       :entity.type/oppo
                                                       current-oppo-pipeline)
                {:oppo/lead-source-v2
                 (named-values/preferred-id :oppo/lead-source-v2
                                            current-oppo-pipeline
                                            (->> @session/account
                                                 :account/preferences
                                                 :preferred-source-category))}))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))
      :entity.type/company (let [initial-value (merge {}
                                                      initial-values
                                                      (when (string? start-value)
                                                        {:asset/name start-value})
                                                      (when (map? start-value)
                                                        start-value))]
                             (swap! draft-state merge initial-value)
                             (swap! live-updates-state initial-value))
      :entity.type/fof-asset (let [initial-value (merge {}
                                                        initial-values
                                                        (when (string? start-value)
                                                          {:asset/name start-value})
                                                        (when (map? start-value)
                                                          start-value))]
                               (swap! draft-state merge initial-value)
                               (swap! live-updates-state initial-value))
      :entity.type/real-estate (let [initial-value (merge {}
                                                          initial-values
                                                          (when (string? start-value)
                                                            {:asset/name start-value})
                                                          (when (map? start-value)
                                                            start-value))]
                                 (swap! draft-state merge initial-value)
                                 (swap! live-updates-state initial-value))
      :entity.type/other-asset (let [initial-value (merge {}
                                                          initial-values
                                                          (when (string? start-value)
                                                            {:asset/name start-value})
                                                          (when (map? start-value)
                                                            start-value))]
                                 (swap! draft-state merge initial-value)
                                 (swap! live-updates-state initial-value))

      :entity.type/fund-commitment (let [initial-value (merge
                                                         {:fund-commitment/owner (:db/id
                                                                                   @session/account)}
                                                         (when (map? start-value)
                                                           start-value))]
                                     (swap! draft-state merge initial-value)
                                     (swap! live-updates-state merge initial-value))

      :entity.type/fund (let [initial-value (merge {}
                                                   initial-values
                                                   (when (string? start-value)
                                                     {:fund/name start-value})
                                                   (when (map? start-value)
                                                     start-value))]
                          (swap! draft-state merge initial-value)
                          (swap! live-updates-state initial-value))

      :entity.type/sales-opportunity
      (let [initial-value
            (merge
              {:sales-opportunity/status   :sales-opportunity.status/active
               :sales-opportunity/pipeline current-sales-opportunity-pipeline
               :sales-opportunity/stage    (->> @session/all-sales-opportunity-stages
                                                (cu/filter-by :sales-opportunity-stage/pipeline
                                                              current-sales-opportunity-pipeline)
                                                (sort-by :common/ord)
                                                (first)
                                                :db/id)
               :sales-opportunity/owner    (let [account-id (:db/id @session/account)
                                                 default-id
                                                 (or (when (get @app-db/active-user-account-roles
                                                                account-id) ;; this should filter out zadmin / sys-admin user
                                                       account-id) ;;TODO : filter out admins that are not pipeline teams' member
                                                     (->> @session/all-accounts
                                                          (filter
                                                            (fn [a]
                                                              (and
                                                                (get @app-db/active-user-account-roles
                                                                     (:db/id a))
                                                                (session/access-to-pipeline
                                                                  a
                                                                  current-sales-opportunity-pipeline)))) ;; Verify with @Mikko P - admin cannot be owner unless team member to pipeline teams
                                                          (first) ;; TODO: something more sensible here ? only when zadmin does something
                                                          :db/id))]
                                             default-id)}
              (when start-value
                (if (string? start-value)
                  {:sales-opportunity/name (cu/str-trim start-value)}
                  start-value)))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))
      :entity.type/exit-opportunity
      (let [initial-value
            (merge
              {:exit-opportunity/status
               :exit-opportunity.status/active
               :exit-opportunity/pipeline
               current-exit-opportunity-pipeline
               :exit-opportunity/stage
               (->> @session/all-exit-opportunity-stages
                    (cu/filter-by :exit-opportunity-stage/pipeline
                                  current-exit-opportunity-pipeline)
                    (sort-by :common/ord)
                    (first)
                    :db/id)
               :exit-opportunity/owner
               (let [account-id (:db/id @session/account)
                     default-id
                     (or (when (get @app-db/active-user-account-roles
                                    ;; this should filter out zadmin / sys-admin user
                                    account-id)
                           ;;TODO : filter out admins that are not pipeline teams' member
                           account-id)
                         (->> @session/all-accounts
                              (filter (fn [a]
                                        (and
                                          (get @app-db/active-user-account-roles
                                               (:db/id a))
                                          (session/access-to-pipeline
                                            a
                                            current-exit-opportunity-pipeline))))
                              (first)
                              :db/id))]
                 default-id)}
              (when start-value
                (if (string? start-value)
                  {:exit-opportunity/name (cu/str-trim start-value)}
                  start-value)))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))
      :entity.type/fundraising-opportunity
      (let [initial-value
            (merge {:fundraising-opportunity/status   :fundraising-opportunity.status/active
                    :fundraising-opportunity/pipeline current-fundraising-opportunity-pipeline
                    :fundraising-opportunity/opportunity-stage
                    (->> @session/all-opportunity-stages
                         (cu/filter-by :opportunity-stage/pipeline
                                       current-fundraising-opportunity-pipeline)
                         (sort-by :common/ord)
                         (first)
                         :db/id)}
                   (when-let [fund (some->> current-fundraising-opportunity-pipeline
                                            (get @session/entities)
                                            :pipeline/fund)]
                     (when (and (session/has-fund-access? fund)
                                (f-permissions/field-write-access? :fundraising-opportunity/fund :entity.type/fundraising-opportunity current-fundraising-opportunity-pipeline))
                       {:fundraising-opportunity/fund fund}))
                   (when start-value
                     (if (string? start-value)
                       {:fundraising-opportunity/opportunity-name (string/trim (str start-value))}
                       start-value)))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))

      :entity.type/named-value
      (when start-value
        (swap! draft-state assoc :named-value/value (string/trim (str start-value)))
        (swap! live-updates-state assoc :named-value/value (string/trim (str start-value))))

      :entity.type/stage
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc :stage/name (string/trim (str start-value)))
              (swap! live-updates-state assoc :stage/name (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))

      :entity.type/opportunity-stage
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc
                     :opportunity-stage/name
                     (string/trim (str start-value)))
              (swap! live-updates-state assoc
                     :opportunity-stage/name
                     (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))
      :entity.type/sales-opportunity-stage
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc
                     :sales-opportunity-stage/name
                     (string/trim (str start-value)))
              (swap! live-updates-state assoc
                     :sales-opportunity-stage/name
                     (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))
      :entity.type/exit-opportunity-stage
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc
                     :exit-opportunity-stage/name
                     (string/trim (str start-value)))
              (swap! live-updates-state assoc
                     :exit-opportunity-stage/name
                     (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))
      :entity.type/holding
      (do
        (reset! draft-state
                (merge
                  {}
                  initial-values
                  (when (f-permissions/field-write-access? :holding/exit-status :entity.type/holding nil)
                    {:holding/exit-status :holding.exit-status/not-exited})
                  (when (f-permissions/field-write-access? :holding/cur-realized-gains
                                                           :entity.type/holding
                                                           nil)
                    {:holding/cur-realized-gains 0})
                  (when (f-permissions/field-write-access? :holding/owner
                                                           :entity.type/holding
                                                           nil)
                    {:holding/owner (:db/id @session/account)})
                  (when start-value
                    (cond (string? start-value) {:holding/name start-value}
                          (map? start-value) start-value))))
        (reset! live-updates-state
                (merge
                  {}
                  (when (f-permissions/field-write-access? :holding/exit-status :entity.type/holding nil)
                    {:holding/exit-status :holding.exit-status/not-exited})
                  (when (f-permissions/field-write-access? :holding/cur-realized-gains
                                                           :entity.type/holding
                                                           nil)
                    {:holding/cur-realized-gains 0})
                  (when (f-permissions/field-write-access? :holding/owner
                                                           :entity.type/holding
                                                           nil)
                    {:holding/owner (:db/id @session/account)})
                  (when start-value
                    (cond (string? start-value) {:holding/name start-value}
                          (map? start-value)
                          start-value)))))
      :entity.type/sale-agreement
      (let [initial-value (merge {}
                                 initial-values
                                 {:sale-agreement/owner (:db/id @session/account)}

                                 (when start-value
                                   (cond (string? start-value)
                                         {:sale-agreement/name (cu/str-trim start-value)}
                                         (map? start-value)
                                         start-value)))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))
      :entity.type/person (when (or start-value
                                    (seq initial-values))
                            (let [parts (string/split (str start-value) #"\s")
                                  email (->> parts
                                             (filter (fn [p]
                                                       (email/valid-email-pattern? p)))
                                             (first))
                                  name-parts (->> parts
                                                  (remove (fn [p]
                                                            (= p email))))
                                  first-name (first name-parts)
                                  last-name (string/join " " (rest name-parts))
                                  init-state (merge {}
                                                    initial-values
                                                    (when-not (string/blank? email)
                                                      {:person/universal-email-address email})
                                                    (when-not (string/blank? first-name)
                                                      {:person/first-name first-name})
                                                    (when-not (string/blank? last-name)
                                                      {:person/last-name last-name}))]
                              (reset! draft-state init-state)
                              (reset! live-updates-state init-state)))
      :entity.type/pipeline
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc :pipeline/name (string/trim (str start-value)))
              (swap! live-updates-state assoc :pipeline/name (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))

      :entity.type/project-counterparty-diligence
      (let [initial-values (merge {} initial-values)]
        (reset! draft-state initial-values)
        (reset! live-updates-state initial-values))

      :entity.type/investor
      (let [initial-value (merge {}
                                 initial-values
                                 (when (f-permissions/field-write-access? :investor/owner
                                                                          :entity.type/investor
                                                                          nil)
                                   {:investor/owner (:db/id @session/account)})
                                 (cond (map? start-value)
                                       start-value
                                       (string? start-value)
                                       {:investor/name (str start-value)}))]
        (swap! draft-state merge initial-value)
        (swap! live-updates-state merge initial-value))
      :entity.type/check-list
      (when start-value
        (if (string? start-value)
          (do (swap! draft-state assoc :check-list/label (string/trim (str start-value)))
              (swap! live-updates-state assoc :check-list/label (string/trim (str start-value))))
          (do (reset! draft-state start-value)
              (reset! live-updates-state start-value))))
      :entity.type/fee (do
                         (reset! draft-state start-value)
                         (reset! live-updates-state start-value))
      :entity.type/liability
      (do (reset! draft-state start-value)
          (reset! live-updates-state start-value))
      :entity.type/survey-profile
      (do (reset! draft-state start-value)
          (reset! live-updates-state start-value))

      :entity.type/kyc-profile
      (do (reset! draft-state start-value)
          (reset! live-updates-state start-value))
      :entity.type/survey-template
      (let [initial-value (merge {:survey-template/show-previous-response-setting
                                  survey-template-schema/show-previous-response-default}
                                 (when (string? start-value)
                                   {:survey-template/label start-value})
                                 (when (map? start-value)
                                   start-value))]
        (reset! draft-state initial-value)
        (reset! live-updates-state initial-value))

      ;; Default. A map `start-value` is the caller stating the initial values
      ;; for the new entity, and every branch above applies it by hand; an
      ;; entity type without a branch used to have it dropped silently. That is
      ;; how survey configuration entities came to be saved orphaned under
      ;; DEV-8200: the backend create fn never saw the parent the caller had
      ;; passed, and the draft never carried the survey type, so pickers scoped
      ;; by `os-filter-terms-fn` resolved no filter and offered every row in the
      ;; tenant.
      ;;
      ;; Applying it here is safe because the `start-value` binding at the top
      ;; of this function has already reduced the map to attributes this account
      ;; may write. A non-map start-value still needs a branch: only the branches
      ;; know which attribute a bare string belongs in.
      (when (map? start-value)
        (reset! draft-state start-value)
        (reset! live-updates-state start-value)))
    ;;this ensures the values are set when field-editors are mounted => allows validation on start:
    (js/setTimeout
      (fn []
        (swap! state dissoc :processing))
      10)))

(defn has-text?
  [txt]
  (->> txt
       (str)
       (string/trim)
       (seq)))

(defn- field-name-of
  [{:keys [tenant-schema raw-schema attribute]}]
  (or (->> (get raw-schema attribute)
           :field/translations
           :en)
      (let [fs (registry/get-field-schema tenant-schema attribute)]
        (loc/field-name attribute fs))))

(defn- with-field-name
  "Prefixes the text of a field error with the name of the field. The text of a
   constraint error, such as \"Mandatory field must contain value\", does not
   name the field, so a list of such texts does not say which field blocks the
   form. A hiccup element is not changed."
  [field-name error]
  (cond
    (string? error)
    (str field-name ": " error)

    (and (sequential? error)
         (not (keyword? (first error))))
    (map (partial with-field-name field-name) error)

    :else
    error))

(defn validate-form
  [state raw-schema]
  ;; todo comments for what this returns (displayable error texts that are shown to user)
  (let [entity-type (:entity-type @state)
        tenant-schema @session/tenant-schema
        mandatory-fields (:mandatory-fields @state)
        live-updates (:live-updates @state)
        draft-entity (:draft-entity @state)
        ;; todo - merge live updates and draft-entity to have single data source
        errors (:errors @state)]
    (->>
      [(some->> errors
                (remove nil?)
                (map (fn [[attribute error]]
                       (with-field-name (field-name-of {:tenant-schema tenant-schema
                                                        :raw-schema    raw-schema
                                                        :attribute     attribute})
                                        error))))
       (->> mandatory-fields
            (keep (fn [mf]
                    (when (cu/blank-value? (or (get live-updates mf)
                                               (get draft-entity mf)))
                      mf)))
            (map (fn [kv]
                   (let [field-name (field-name-of {:tenant-schema tenant-schema
                                                    :raw-schema    raw-schema
                                                    :attribute     kv})]
                     (loc/tr :mandatory-field/value-not-set [field-name]))))) ;; end mandatory fields check
       (case entity-type                                    ;; validate against both live updates and draft entity ??
         :entity.type/person
         (when-not (or (has-text? (:person/first-name live-updates))
                       (has-text? (:person/last-name live-updates))
                       (has-text? (:person/universal-email-address
                                    live-updates))
                       (some #{:person/first-name :person/last-name :person/universal-email-address}
                             mandatory-fields))
           (loc/tr :person-first-last-or-email-missing))
         nil)]
      (remove nil?)
      (mapcat (fn [err]
                (cond (and (coll? err)
                           (keyword? (first err)))
                      [err]
                      (coll? err)
                      (seq err)
                      :else
                      [err])))
      (distinct))))

(defn on-submit!
  [state draft-state on-success! on-error!]
  (entity-actions/*->create
    (remove-empty-vals
      (merge @draft-state
             {:entity/type (:entity-type @state)}
             (when (= :entity.type/named-value (:entity-type @state))
               {:named-value/attribute (:attribute @state)})))
    (fn [resp]
      (let [entity (:body resp)
            id (:db/id entity)]
        (js/console.log "Entity create response : " id " >> " resp)
        (if id
          (do (swap! session/entities assoc id entity)
              (deref session/entities)
              (js/console.log "Updated session entities : " entity))
          (logging/warn "Unexpected create response, did not receive entity " resp))
        (on-success! entity resp)))
    on-error!))

(defn resolve-selected-pipeline-id
  [{:keys [entity-type creation-pipeline-id draft-state]}]
  (let [pipeline-key (pipeline/pipeline-key entity-type)]
    (when pipeline-key
      (or creation-pipeline-id
          ;; Explicit selection (UI) is stored in draft state
          (get @draft-state pipeline-key)
          (case entity-type
            :entity.type/oppo @session/selected-oppo-pipeline
            :entity.type/fundraising-opportunity @session/selected-froppo-pipeline
            :entity.type/sales-opportunity @session/selected-sales-oppo-pipeline
            :entity.type/exit-opportunity @session/selected-exit-oppo-pipeline
            nil)))))

(defn resolve-mandatory-fields
  [{:keys [entity-type creation-pipeline-id draft-state
           state mandatory-fields target-assets-in-use]}]
  (let [selected-pipeline-id (resolve-selected-pipeline-id
                               {:entity-type          entity-type
                                :creation-pipeline-id creation-pipeline-id
                                :draft-state          draft-state})
        capman-re? (and (tenant-customizations/capman?)
                        (tenant-customizations/capman-is-real-estate-pipeline?
                          selected-pipeline-id))
        mandatory-fields-list (-> (concat (current-settings/mandatory-fields
                                            {:entity-type              entity-type
                                             :state                    state
                                             :default-mandatory-fields mandatory-fields
                                             :selected-pipeline-id     selected-pipeline-id})
                                          (when (and (= entity-type :entity.type/pipeline)
                                                     target-assets-in-use)
                                            [:pipeline/target-asset-types]))
                                  (set)
                                  (disj :person/universal-email-address
                                        (when capman-re?
                                          :oppo/lead-source-v2))
                                  (vec))
        resolved-mandatory-fields (if-not mandatory-fields
                                    mandatory-fields-list
                                    (->> mandatory-fields-list
                                         (sort-by (fn [f]
                                                    (fu/index-of mandatory-fields f)))))]
    resolved-mandatory-fields))
